import { Injectable } from '@angular/core';
import { fetchAuthSession } from 'aws-amplify/auth';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class CsrfService {
  private csrfToken: string | null = null;
  private sessionId: string | null = null;
  private tokenFetchPromise: Promise<string | null> | null = null;
  private readonly TOKEN_STORAGE_KEY = 'csrf_token_secure';
  private tokenExpiryTime: number | null = null;

  constructor() {}

  /**
   * Get current CSRF token from secure storage
   * Uses sessionStorage instead of cookies for better security
   */
  getCurrentToken(): string | null {
    
    // First check memory
    if (this.csrfToken && this.isTokenValid()) {
      
      return this.csrfToken;
    }
    
    // Check sessionStorage (more secure than cookies)
    const storedData = sessionStorage.getItem(this.TOKEN_STORAGE_KEY);
    if (storedData) {
      try {
        const parsed = JSON.parse(storedData);
        if (parsed.token && parsed.expiry && Date.now() < parsed.expiry) {
          this.csrfToken = this.deobfuscateToken(parsed.token);
          this.tokenExpiryTime = parsed.expiry;
          
          // Also restore session ID if available (for anonymous sessions)
          if (parsed.sessionId) {
            this.sessionId = this.deobfuscateToken(parsed.sessionId);
          }
          
          return this.csrfToken;
        } else {
          // Token expired, clear it
          this.clearToken();
        }
      } catch (error) {
        console.error('❌ [CSRF] Failed to parse stored CSRF token:', error);
        this.clearToken();
      }
    } else {
      console.log('📭 [CSRF] No data in SessionStorage');
    }
    
    console.log('❌ [CSRF] No valid token found anywhere');
    return null;
  }

  /**
   * Fetch a new CSRF token from the server
   */
  async fetchToken(): Promise<string | null> {
    // Prevent multiple concurrent requests
    if (this.tokenFetchPromise) {
      return this.tokenFetchPromise;
    }

    this.tokenFetchPromise = this.fetchTokenInternal();
    const token = await this.tokenFetchPromise;
    this.tokenFetchPromise = null;
    
    return token;
  }

  /**
   * Internal method to fetch CSRF token with enhanced security
   */
  private async fetchTokenInternal(): Promise<string | null> {
    try {
      
      const session = await fetchAuthSession();
      const authToken = session.tokens?.idToken?.toString();
      
      let headers: Record<string, string> = {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest', // Additional CSRF protection
        'Cache-Control': 'no-cache, no-store, must-revalidate' // Prevent caching
      };

      // Add authorization header if authenticated
      if (authToken) {
        headers['Authorization'] = `Bearer ${authToken}`;
        
      } else {
        
      }

      const response = await fetch(`${environment.baseUrl}/csrf-token`, {
        method: 'GET',
        headers
      });

      if (response.ok) {
        const data = await response.json();
        const token = data.csrfToken;
        const sessionId = data.sessionId; // For anonymous users
        const expiryTime = Date.now() + (23 * 60 * 60 * 1000); // 23 hours (slightly less than server TTL)
        
        // Store securely in sessionStorage
        this.storeTokenSecurely(token, expiryTime, sessionId);
        
        
        if (sessionId) {
        }
        return token;
      } else {
        console.error('❌ Failed to fetch CSRF token:', response.status, response.statusText);
        return null;
      }
    } catch (error) {
      console.error('❌ Error fetching secure CSRF token:', error);
      return null;
    }
  }

  /**
   * Store token securely in sessionStorage with encryption-like obfuscation
   */
  private storeTokenSecurely(token: string, expiryTime: number, sessionId?: string): void {
    this.csrfToken = token;
    this.tokenExpiryTime = expiryTime;
    this.sessionId = sessionId || null;
    
    // Store in sessionStorage with basic obfuscation
    const tokenData: any = {
      token: this.obfuscateToken(token),
      expiry: expiryTime,
      timestamp: Date.now()
    };
    
    // Include session ID for anonymous users
    if (sessionId) {
      tokenData.sessionId = this.obfuscateToken(sessionId);
    }
    
    sessionStorage.setItem(this.TOKEN_STORAGE_KEY, JSON.stringify(tokenData));
  }

  /**
   * Basic token obfuscation (not real encryption, but better than plain text)
   */
  private obfuscateToken(token: string): string {
    // Simple base64 encoding with some character substitution
    const encoded = btoa(token);
    return encoded.split('').map(char => {
      const code = char.charCodeAt(0);
      return String.fromCharCode(code + 1);
    }).join('');
  }

  /**
   * Deobfuscate token
   */
  private deobfuscateToken(obfuscatedToken: string): string {
    try {
      const decoded = obfuscatedToken.split('').map(char => {
        const code = char.charCodeAt(0);
        return String.fromCharCode(code - 1);
      }).join('');
      return atob(decoded);
    } catch (error) {
      console.error('Failed to deobfuscate token:', error);
      return '';
    }
  }

  /**
   * Check if current token is still valid
   */
  private isTokenValid(): boolean {
    return this.tokenExpiryTime ? Date.now() < this.tokenExpiryTime : false;
  }

  /**
   * Initialize CSRF token on app startup
   */
  async initializeToken(): Promise<void> {
    try {
      // Check if we already have a valid token
      const existingToken = this.getCurrentToken();
      if (existingToken) {
        return;
      }

      // Fetch new token
      
      const newToken = await this.fetchToken();
      if (newToken) {
      } else {
        console.log('❌ [CSRF] Failed to fetch new token');
      }
    } catch (error) {
      console.error('❌ [CSRF] Failed to initialize secure CSRF token:', error);
    }
  }

  /**
   * Get current session ID (for anonymous requests)
   */
  getSessionId(): string | null {
    return this.sessionId;
  }

  /**
   * Clear stored CSRF token securely
   */
  clearToken(): void {
    this.csrfToken = null;
    this.sessionId = null;
    this.tokenExpiryTime = null;
    sessionStorage.removeItem(this.TOKEN_STORAGE_KEY);
    
  }

  /**
   * Get token with automatic refresh if needed
   */
  async getValidToken(): Promise<string | null> {
    const currentToken = this.getCurrentToken();
    if (currentToken) {
      return currentToken;
    }
    
    // Token missing or expired, fetch new one
    return await this.fetchToken();
  }

  /**
   * Enhanced security: Check if request is potentially malicious
   */
  validateRequestOrigin(): boolean {
    const currentOrigin = window.location.origin;
    const allowedOrigins = [
      'http://localhost:4200',
      'https://your-production-domain.com'
    ];
    
    return allowedOrigins.includes(currentOrigin);
  }
}